import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, Pencil, UserCheck, ExternalLink } from 'lucide-react'
import { api, type IntegratorRow } from '../api/client'
import { useActiveIntegrator } from '../auth/active-integrator'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

export function IntegratorsPage() {
  const qc = useQueryClient()
  const nav = useNavigate()
  const active = useActiveIntegrator()
  const { data, isLoading, error } = useQuery({
    queryKey: ['integrators'],
    queryFn: () => api.integrators.list(100, 0),
  })

  const [editing, setEditing] = useState<IntegratorRow | null>(null)

  const toggleSigning = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.integrators.setSigning(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrators'] }),
  })

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    )
  }
  if (error) {
    return <div className="text-destructive">Failed to load: {(error as Error).message}</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrators</h1>
        <p className="text-sm text-muted-foreground">
          Pick an integrator to operate within, or open one for details and key rotation.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Callback URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signing</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((it) => {
                const isActive = active.integratorId === it.id
                return (
                  <TableRow key={it.id}>
                    <TableCell>
                      <div className="font-medium">{it.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{it.slug ?? '—'}</div>
                    </TableCell>
                    <TableCell className="max-w-xs break-all font-mono text-xs text-muted-foreground">
                      {it.callbackUrl ?? <span className="italic">none</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={it.status === 'active' ? 'default' : 'secondary'}>
                        {it.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={it.signingEnabled ?? true}
                          disabled={toggleSigning.isPending}
                          onChange={(e) =>
                            toggleSigning.mutate({ id: it.id, enabled: e.target.checked })
                          }
                        />
                        <span className="text-xs text-muted-foreground">
                          {it.signingEnabled === false ? 'disabled' : 'enabled'}
                        </span>
                      </label>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant={isActive ? 'secondary' : 'default'}
                          disabled={isActive}
                          onClick={() => active.setActive(it.id, it.name)}
                        >
                          {isActive ? <><Check className="mr-1 h-3.5 w-3.5" /> Active</> : <><UserCheck className="mr-1 h-3.5 w-3.5" /> Operate as</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => nav(`/integrators/${it.id}`)}>
                          <ExternalLink className="mr-1 h-3.5 w-3.5" />
                          Open
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(it)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <EditIntegratorModal
          integrator={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            qc.invalidateQueries({ queryKey: ['integrators'] })
          }}
        />
      )}
    </div>
  )
}

function EditIntegratorModal(props: {
  integrator: IntegratorRow
  onClose: () => void
  onSaved: () => void
}) {
  const { integrator, onClose, onSaved } = props
  const [name, setName] = useState(integrator.name)
  const [callbackUrl, setCallbackUrl] = useState(integrator.callbackUrl ?? '')

  const saveProfile = useMutation({
    mutationFn: (patch: { name?: string; callbackUrl?: string }) =>
      api.integrators.updateProfile(integrator.id, patch),
  })

  const dirty =
    name !== integrator.name || callbackUrl !== (integrator.callbackUrl ?? '')

  const submit = async () => {
    const patch: { name?: string; callbackUrl?: string } = {}
    if (name !== integrator.name) patch.name = name
    if (callbackUrl !== (integrator.callbackUrl ?? '')) patch.callbackUrl = callbackUrl
    if (Object.keys(patch).length > 0) await saveProfile.mutateAsync(patch)
    onSaved()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">Edit integrator</h2>
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Name</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Callback URL</span>
              <Input
                type="url"
                value={callbackUrl}
                placeholder="https://integrator.example.com/webhooks/reset"
                onChange={(e) => setCallbackUrl(e.target.value)}
                className="font-mono text-xs"
              />
              <span className="text-xs text-muted-foreground">
                Empty clears the URL — callbacks stop being delivered.
              </span>
            </label>
          </div>
          {saveProfile.error && (
            <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              {(saveProfile.error as Error).message}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              disabled={!dirty || saveProfile.isPending}
              onClick={() => void submit()}
            >
              {saveProfile.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
